import React, { useContext } from "react";
import {
    createFailure,
    createValidationResult,
    FailureSeverity,
    ShimmeredDetailsList,
    useNotifications,
    usePerformMultipleActions,
    ValidationFailure
} from "sprint-common-web";

import { useCloudFileApi } from "../../../../../../common/api/master-api/cloud-files/useCloudFileApi";
import { CloudFileRejectionData } from "../../../../common/components/reject-cloud-file/CloudFileRejectionData";
import { RejectCloudFileDialogForm } from "../../../../common/components/reject-cloud-file/RejectCloudFileDialogForm";
import { CloudFileStatus, isInvalidStatusForReject } from "../../../../common/types/CloudFileStatus";
import { CloudFileListingContext } from "../../../CloudFileListingContext";
import { createRejectCloudFilesColumns } from "./createRejectCloudFilesColumns";
import { RejectCloudFileItem } from "./RejectCloudFileItem";

type RejectMultipleCloudFilesDialogProps = {
    selectedItems: RejectCloudFileItem[];
    onClose(): void;
};

export const RejectMultipleCloudFilesDialog: React.VFC<RejectMultipleCloudFilesDialogProps> = ({
    selectedItems,
    onClose
}: RejectMultipleCloudFilesDialogProps) => {
    const { rejectCloudFile } = useCloudFileApi();
    const { updateCloudFileStatus } = useContext(CloudFileListingContext);

    const { success, error: errorNotification } = useNotifications();
    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions({
        isAvailable: (item) => !isInvalidStatusForReject(item.status),
        selectedItems
    });

    const reject = async (data: CloudFileRejectionData) => {
        const results = await rejectAllItems(data);

        if (results.every(({ status }) => status === "fulfilled")) {
            onClose();

            success("Rejecting complete");
        } else errorNotification("Something went wrong while rejecting cloud files");
    };

    const rejectAllItems = async (data: CloudFileRejectionData) => {
        return performAllItems(async (item) => {
            await rejectCloudFile({
                cloudFileId: item.id,
                reasonId: data.reason.id,
                details: data.details,
                courseOfActionId: data.courseOfAction?.id,
                fixedBillingId: data.fixedBilling?.id
            });

            updateCloudFileStatus(item.id, CloudFileStatus.Rejected, {
                courseOfAction: data.courseOfAction.name,
                reason: data.reason.name,
                details: data.details,
                fixedBilling: data.fixedBilling?.name
            });
        });
    };

    const columns = createRejectCloudFilesColumns();
    const formHasErrors = createValidationResult([validateDiscardedItems(items, selectedItems.length)]);
    const areInvalidCloudFiles = !items.length;

    return (
        <RejectCloudFileDialogForm
            title={"Reject cloud files"}
            description={"Are you sure you want to start rejecting listed cloud files?"}
            onSubmit={reject}
            onClose={onClose}
            width={"65%"}
            height={600}
            areActionsDisabled={areInvalidCloudFiles || isActionInProgress}
            validationResult={formHasErrors}
        >
            {!!items.length && <ShimmeredDetailsList columns={columns} items={items} layoutMode="fixedColumns" />}
        </RejectCloudFileDialogForm>
    );
};

const discardedCloudFilesInfoMessage = `Cloud files with statuses Prep For Qc, Rejected or Qc Done have been discarded`;

const validateDiscardedItems = (items: RejectCloudFileItem[], selectedItemsCount: number): ValidationFailure | void => {
    if (!items.length) {
        return createFailure(
            `${discardedCloudFilesInfoMessage}. Nothing to reject.`,
            "no-orders-to-deliver",
            FailureSeverity.Error
        );
    } else if (items.length < selectedItemsCount) {
        return createFailure(discardedCloudFilesInfoMessage, "orders-discarded-from-delivery", FailureSeverity.Warning);
    }
};
